<template>
  <div class="assembly-instructions-media-slider-fi">
    <div class="assembly-instructions-media-slider-fi__slider-controls">
      <div
        v-if="isLG"
        :set="
          (sliders = {
            forwardDisabled: isFirstSlide,
            backDisabled: isLastSlide,
          })
        "
        class="assembly-instructions-media-slider-fi__slider-arrows"
      >
        <template v-if="isSlider">
          <span
            :class="{
              'assembly-instructions-media-slider-fi__slider-prev': true,
              'assembly-instructions-media-slider-fi__slider--inactive': sliders.forwardDisabled,
            }"
            :aria-disabled="sliders.forwardDisabled"
            role="button"
            @click="prevSlide"
          >
            <arrow-icon />
          </span>
          <span
            :class="{
              'assembly-instructions-media-slider-fi__slider-next': true,
              'assembly-instructions-media-slider-fi__slider--inactive': sliders.backDisabled,
            }"
            :aria-disabled="sliders.backDisabled"
            role="button"
            @click="nextSlide"
          >
            <arrow-icon />
          </span>
        </template>
      </div>
      <div
        v-else
        class="assembly-instructions-media-slider-fi__indication"
      >
        <span class="assembly-instructions-media-slider-fi__indication--current">
          {{ visibleCount }}
        </span>
        <span class="assembly-instructions-media-slider-fi__indication--total">
          / {{ mediaItems.length }}
        </span>
      </div>
    </div>

    <carousel-fi
      v-if="mediaItems.length"
      ref="carousel"
      v-intersect.once="onIntersect"
      class="assembly-instructions-media-slider-fi__slider"
      :settings="sliderSettings"
      no-arrows
      @after-change="onAfterChange"
    >
      <carousel-slide-fi
        v-for="(media, index) in mediaItems"
        :key="media.Url"
        class="fi-relative"
      >
        <span class="assembly-instructions-media-slider-fi__badge">
          {{ index + 1 }}
        </span>
        <cloud-image-fi
          :src="media.Url"
          :alt="media.label"
          :sizes="{
            xxs: { h: 115 },
            xs: { h: 115 },
            sm: { h: 125 },
            md: { h: 142 },
            lg: { h: 142 },
            xl: { h: 142 },
          }"
          params="func=bound"
          do-not-replace-url
        />
      </carousel-slide-fi>
    </carousel-fi>
  </div>
</template>

<script>
import { ArrowIcon, CarouselFi, CarouselSlideFi, CloudImageFi } from 'atoms';
import breakpointMixin from 'mixins/breakpointMixin';
import { mapGetters } from 'vuex';

export default {
  name: 'AssemblyInstructionsMediaSliderFi',
  components: {
    ArrowIcon,
    CarouselFi,
    CarouselSlideFi,
    CloudImageFi,
  },
  mixins: [breakpointMixin],
  props: {
    mediaItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isSlider: false,
      isFirstSlide: true,
      isLastSlide: false,
      visibleCount: 1,
      visibleItems: 1,
      slideGapInPixels: 24,
    };
  },
  computed: {
    ...mapGetters({
      windowDimensions: 'layout/getWindowDimensions',
    }),
    sliderSettings() {
      return {
        drag: this.isSlider,
        autoWidth: true,
        rewind: false,
        pagination: false,
        perPage: this.visibleItems,
        perMove: 1, // OR 'this.visibleItems' to scroll all visible items away
        gap: this.slideGapInPixels,
      };
    },
  },
  watch: {
    windowDimensions: {
      deep: true,
      handler() {
        this.determineVisibleItems(this.$refs.carousel?.$el?.offsetWidth);
      },
    },
  },
  methods: {
    onIntersect() {
      this.determineVisibleItems(this.$refs.carousel?.$el?.offsetWidth);
    },
    determineVisibleItems(sliderWidth) {
      if (!sliderWidth) {
        return;
      }

      const splideSlides = this.$refs.carousel.$refs.carousel.splide.Components.Elements.slides;

      let sum = 0;
      splideSlides.every((el, idx) => {
        sum += el.offsetWidth;
        if (idx < splideSlides.length - 1) {
          sum += this.slideGapInPixels;
        }

        this.isSlider = sum > sliderWidth;
        this.visibleItems = idx;

        return !this.isSlider;
      });

      if (!this.isLG) {
        this.updateCurrentItemCount();
      }
    },
    updateCurrentItemCount() {
      const findClosestIndex = (arr, targetValue) => {
        return arr.reduce((acc, current, index) => {
          const currentDifference = Math.abs(current - targetValue);
          const minDifference = Math.abs(arr[acc] - targetValue);

          return currentDifference < minDifference ? index : acc;
        }, 0);
      };

      const splideSlides = this.$refs.carousel.$refs.carousel.splide.Components.Elements.slides;
      const splideSlideEdgesRight = splideSlides.map((el) => el.getBoundingClientRect().right);
      const sliderEdgeRight = this.$refs.carousel.$el.getBoundingClientRect().right;

      this.visibleCount = findClosestIndex(splideSlideEdgesRight, sliderEdgeRight) + 1;
    },
    nextSlide() {
      if (this.$refs.carousel) {
        this.$refs.carousel.next();
      }
    },
    prevSlide() {
      if (this.$refs.carousel) {
        this.$refs.carousel.prev();
      }
    },
    onAfterChange() {
      const splide = this.$refs.carousel.$refs.carousel.splide;
      this.isFirstSlide = splide?.index === 0;
      this.isLastSlide = splide?.index + this.visibleItems === this.mediaItems.length;
      this.updateCurrentItemCount();
    },
  },
};
</script>

<style lang="scss" scoped>
@import './assembly-instructions-media-slider-fi';
</style>
